import { Injectable } from '@angular/core';
import {
  DialogCloseResult,
  DialogService,
} from '@progress/kendo-angular-dialog';
import { firstValueFrom, from, Observable } from 'rxjs';
import { KendoAlertComponent } from './kendo-alert.component';
import { KendoAlertButtonBase } from './kendo-alert.models';
import { KendoPromptType } from '../kendo-prompt/kendo-prompt.models';
import { KendoPromptComponent } from '../kendo-prompt/kendo-prompt.component';

/**
 * A service for displaying Kendo UI-styled alert and prompt dialogs.
 * 
 * This service provides methods for displaying various types of dialogs:
 * - Alert dialogs with customizable buttons
 * - Confirmation dialogs with Yes/No options
 * - Prompt dialogs for user input
 * 
 * All dialog methods return Promises that resolve when the user interacts with the dialog.
 * 
 * @example
 * ```typescript
 * // Simple alert
 * this.kendoAlertService.alertAsync('Operation completed successfully');
 * 
 * // Confirmation dialog
 * const confirmed = await this.kendoAlertService.confirmAsync('Are you sure?');
 * if (confirmed) {
 *   // Proceed with action
 * }
 * 
 * // Custom alert with multiple buttons
 * const result = await this.kendoAlertService.alertAsync(
 *   'Choose an option:',
 *   [
 *     { label: 'Save', primary: true },
 *     { label: 'Don\'t Save' },
 *     { label: 'Cancel' }
 *   ],
 *   'Document Modified'
 * );
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class KendoAlertService {
  /**
   * Creates a new instance of the KendoAlertService.
   * @param dialogService The Kendo UI DialogService for creating dialogs.
   */
  constructor(private dialogService: DialogService) {}

  /**
   * Displays a prompt dialog for collecting user input.
   * 
   * @param msg The message to display in the prompt dialog.
   * @param title The title of the dialog window.
   * @param type The type of input to collect from the user.
   * @param value The initial value to display in the input field.
   * @returns A Promise that resolves to the user's input value, or null if canceled.
   */
  promptAsync(
    msg: string,
    title: string = '',
    type: KendoPromptType,
    value: number | string | null = null
  ): Promise<number | string | null> {
    let dialogRef = this.dialogService.open({
      title: title,
      content: KendoPromptComponent,
      actions: [{ text: 'OK', primary: true }, { text: 'Cancel' }],
      width: 400,
    });

    let instance = dialogRef.content.instance as KendoPromptComponent;
    instance.msg = msg;
    instance.value = value;

    let result = new Observable<number | string | null>((observer) => {
      dialogRef.result.subscribe((dialogResult) => {
        console.log(dialogResult);
        if (dialogResult instanceof DialogCloseResult) {
          console.log('DialogCloseResult');
          observer.next(null);
          observer.complete();
        } else {
          observer.next(instance.value);
          observer.complete();
        }
      });
    });

    return firstValueFrom(result);
  }

  /**
   * Displays a prompt dialog for collecting user input.
   * Returns an Observable for reactive programming patterns.
   * 
   * @param msg The message to display in the prompt dialog.
   * @param title The title of the dialog window.
   * @param type The type of input to collect from the user.
   * @param value The initial value to display in the input field.
   * @returns An Observable that emits the user's input value, or null if canceled.
   */
  prompt(
    msg: string,
    title: string = '',
    type: KendoPromptType,
    value: number | string | null = null
  ): Observable<number | string | null> {
    return from(
      this.promptAsync(msg, title, type, value));
  }

  /**
   * Displays an alert dialog with customizable buttons.
   * 
   * @param msg The message to display in the alert dialog. Can contain HTML content.
   * @param buttons An array of button configurations to display. Defaults to a single "OK" button.
   * @param title The title of the dialog window. Defaults to "Alert".
   * @returns A Promise that resolves to the label of the button clicked, or "close" if dismissed.
   */
  alertAsync(
    msg: string,
    buttons: KendoAlertButtonBase[] = [{ label: 'OK' }],
    title: string = 'Alert'
  ): Promise<string> {
    let dialogRef = this.dialogService.open({
      title: title,
      content: KendoAlertComponent,
      actions: buttons.map((button) => {
        return { text: button.label, primary: button.primary };
      }),
      width: 400,
    });

    let instance = dialogRef.content.instance as KendoAlertComponent;
    instance.msg = msg;

    let result = new Observable<string>((observer) => {
      dialogRef.result.subscribe((dialogResult) => {
        console.log(dialogResult);
        if (dialogResult instanceof DialogCloseResult) {
          console.log('DialogCloseResult');
          observer.next('close');
          observer.complete();
        } else {
          observer.next(dialogResult.text);
          observer.complete();
        }
      });
    });

    return firstValueFrom(result);
  }

  /**
   * Displays a confirmation dialog with Yes/No buttons.
   * 
   * @param msg The message to display in the confirmation dialog. Can contain HTML content.
   * @returns A Promise that resolves to true if "Yes" was clicked, false otherwise.
   */
  confirmAsync(msg: string): Promise<boolean> {
    return this.alertAsync(msg, [
      { label: 'Yes', primary: true },
      { label: 'No' },
    ]).then((result) => {
      return result === 'Yes';
    });
  }
}
